import { cn } from '@core/lib/cn';

export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /**
   * Accessible label. Defaults to none: a spinner inside a button that already
   * says "Placing order…" must not be announced twice. Pass a label only when the
   * spinner is the sole indication that something is happening.
   */
  label?: string;
}

const SIZES = {
  sm: 'h-4 w-4 border-2',
  md: 'h-5 w-5 border-2',
  lg: 'h-8 w-8 border-[3px]',
} as const;

/**
 * A spinner is never a screen on its own — see Skeleton for loading content and
 * ErrorState for failures. This exists for inside-a-control feedback.
 */
export function Spinner({ size = 'md', className, label }: SpinnerProps) {
  return (
    <span
      className={cn(
        'inline-block shrink-0 animate-spin rounded-full border-current border-t-transparent',
        'motion-reduce:animate-[spin_1.5s_linear_infinite]',
        SIZES[size],
        className,
      )}
      role={label ? 'status' : undefined}
      aria-hidden={label ? undefined : true}
    >
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
}
